import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { FancyCheckbox } from "@/components/ui/fancy-checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus } from "@phosphor-icons/react";
import { Control, UseFormSetValue, useController } from "react-hook-form";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface LessonType {
  value: string;
  description: string;
  durations: string[];
}

interface Template {
  id: string;
  name: string;
  tags: Array<{
    id: string;
    category: string;
    subcategory?: string;
    tagText: string;
    general: boolean;
  }>;
}

interface LessonTypeSelectorProps {
  type: LessonType;
  typeIndex: number;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  availableTemplates: Template[];
  isTemplateFeatureEnabled: boolean;
  onTemplateUpload: () => void;
  getBadgeStyles: (category?: string, subcategory?: string) => string;
  onTypeCheck?: (typeIndex: number, checked: boolean) => void;
}

export const LessonTypeSelector = memo(function LessonTypeSelector({
  type,
  typeIndex,
  control,
  setValue,
  availableTemplates,
  isTemplateFeatureEnabled,
  onTemplateUpload,
  getBadgeStyles,
  onTypeCheck,
}: LessonTypeSelectorProps) {
  const { t } = useTranslation("lessonLab");
  const { field: checkedField } = useController({
    name: `type.${typeIndex}.checked`,
    control,
  });
  const isChecked = !!checkedField.value;

  return (
    <div className="flex flex-col items-start justify-between gap-2">
      <FormField
        key={`${type.value}-checkbox`}
        control={control}
        name={`type.${typeIndex}.checked`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-center flex-shrink gap-2">
            <FormControl>
              <FancyCheckbox
                variant="monochrome"
                checked={field.value}
                className="rounded-full"
                onCheckedChange={(checked) => {
                  setValue(`type.${typeIndex}.checked`, Boolean(checked));
                  if (onTypeCheck) {
                    onTypeCheck(typeIndex, Boolean(checked));
                  }
                }}
              />
            </FormControl>
            <FormLabel className="!text-[18px]">
              {t(
                `types.${type.value.replace(/\s+/g, "").replace(/^./, (c) => c.toLowerCase())}`
              )}
              <FormDescription className="!text-content !text-light-grey">
                {t(
                  `types.${type.value.replace(/\s+/g, "").replace(/^./, (c) => c.toLowerCase())}.description`
                )}
              </FormDescription>
            </FormLabel>
          </FormItem>
        )}
      />

      {isChecked && (
        <div className="flex gap-3 flex-col @md:flex-row ml-6 mt-2">
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
            <FormField
              key={`${type.value}-duration`}
              control={control}
              name={`type.${typeIndex}.duration`}
              render={({ field }) => {
                const currentValue = field.value || "";
                const isPresetOption = type.durations.includes(currentValue);
                const isOtherSelected =
                  currentValue === "other" || !isPresetOption;

                const getDurationParts = () => {
                  if (isPresetOption) return { value: "", unit: "minutes" };
                  const parts = currentValue.split(" ");
                  return {
                    value: parts[0] || "",
                    unit: parts[1] || "minutes",
                  };
                };

                const { value: durationValue, unit: durationUnit } =
                  getDurationParts();

                return (
                  <FormItem className="flex flex-col min-w-[200px] gap-2">
                    <FormLabel className="text-sm font-semibold text-gray-700">
                      {t("duration.label")}
                    </FormLabel>
                    {!isOtherSelected ? (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white border-gray-300 rounded-lg shadow-sm">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {type.durations.map((duration) => (
                            <SelectItem key={duration} value={duration}>
                              {duration}
                            </SelectItem>
                          ))}
                          <SelectItem value="other">
                            {t("duration.other")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="w-20 bg-white border-gray-300 shadow-sm"
                          value={durationValue}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            field.onChange(`${newValue} ${durationUnit}`);
                          }}
                        />
                        <Select
                          value={durationUnit}
                          onValueChange={(unit) => {
                            field.onChange(`${durationValue} ${unit}`);
                          }}
                        >
                          <SelectTrigger className="bg-white border-gray-300 shadow-sm w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minutes">
                              {t("duration.minutes")}
                            </SelectItem>
                            <SelectItem value="hours">
                              {t("duration.hours")}
                            </SelectItem>
                            <SelectItem value="days">
                              {t("duration.days")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </FormItem>
                );
              }}
            />
          </div>

          {/* Template Selection */}
          {isTemplateFeatureEnabled && (
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
              <FormField
                key={`${type.value}-template`}
                control={control}
                name={`type.${typeIndex}.selectedTemplate`}
                render={({ field }) => (
                  <FormItem className="flex flex-col min-w-[250px] gap-2">
                    <FormLabel className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      {t("template.label")}
                      {availableTemplates.length > 0 && (
                        <Badge
                          variant="outline"
                          className="text-purple-800 bg-purple-100 border-purple-200"
                        >
                          {t("template.available", {
                            count: availableTemplates.length,
                          })}
                        </Badge>
                      )}
                    </FormLabel>
                    {availableTemplates.length > 0 ? (
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="bg-white border-gray-300 rounded-lg shadow-sm">
                            <SelectValue
                              placeholder={t("template.placeholder")}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="__no_template__">
                            {t("template.none")}
                          </SelectItem>
                          {availableTemplates.map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              <div className="flex items-center gap-2">
                                <span>{template.name}</span>
                                <div className="flex gap-1">
                                  {template.tags.slice(0, 1).map((tag) => (
                                    <Badge
                                      key={tag.id}
                                      variant="outline"
                                      className={`${getBadgeStyles(tag.category, tag.subcategory)} text-xs`}
                                    >
                                      {tag.tagText}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onTemplateUpload}
                        className="self-start gap-2 bg-white border-gray-300 shadow-sm hover:bg-gray-50"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                        {t("template.upload")}
                      </Button>
                    )}
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});
